import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [slidesContainer]
 *
 * @extends {Component<Refs>}
 */
class PressQuotes extends Component {
  #current = 0;
  #intervalId = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.#syncContainerHeight();
    window.addEventListener('resize', this.#syncContainerHeight);

    this.#showSlide(0);

    if (!this.#isEnabled) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#start();

    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    window.removeEventListener('resize', this.#syncContainerHeight);
    this.removeEventListener('mouseenter', this.#pause);
    this.removeEventListener('mouseleave', this.#resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  get #slides() {
    return this.refs.slides ?? [];
  }

  get #isEnabled() {
    return !this.hasAttribute('data-disabled') && this.#slides.length > 1;
  }

  get #slidesContainer() {
    return this.refs.slidesContainer;
  }

  get #autoplayDelay() {
    const value = Number.parseInt(this.getAttribute('autoplay') ?? '', 10);
    if (Number.isNaN(value)) return 6000;

    return value * 1000;
  }

  #start = () => {
    if (!this.#isEnabled) return;
    this.#stop();

    this.#intervalId = window.setInterval(() => {
      if (document.hidden) return;
      this.#next();
    }, this.#autoplayDelay);
  };

  #stop = () => {
    if (this.#intervalId === undefined) return;
    window.clearInterval(this.#intervalId);
    this.#intervalId = undefined;
  };

  #pause = () => {
    this.#stop();
  };

  #resume = () => {
    this.#start();
  };

  #next() {
    if (!this.#isEnabled) return;

    this.#current = (this.#current + 1) % this.#slides.length;
    this.#showSlide(this.#current);
  }

  /**
   * @param {number} index
   */
  #showSlide(index) {
    for (const [slideIndex, slide] of this.#slides.entries()) {
      slide.setAttribute('aria-hidden', `${slideIndex !== index}`);
    }
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#pause();
      return;
    }

    this.#resume();
  };

  #syncContainerHeight = () => {
    const slidesContainer = this.#slidesContainer;
    if (!slidesContainer) return;
    if (this.#slides.length === 0) return;

    let maxHeight = 0;

    for (const slide of this.#slides) {
      const slideHeight = slide.getBoundingClientRect().height;
      maxHeight = Math.max(maxHeight, slideHeight);
    }

    if (maxHeight === 0) return;
    slidesContainer.style.minBlockSize = `${Math.ceil(maxHeight)}px`;
  };
}

if (!customElements.get('press-quotes-component')) {
  customElements.define('press-quotes-component', PressQuotes);
}
